import { locateInPath } from "../data/navigation/studyPaths";

/*
 * Which study path the reader is on, if any.
 *
 * The URL decides. /exodus?path=the-wilderness-stage is on the path; plain
 * /exodus is not, even for someone who was walking that path a moment ago.
 * Every link the path itself offers -- next, previous, resume from the home
 * page -- carries the parameter, so walking the path keeps it. Reaching a page
 * any other way, from the nav or a search result, is a deliberate departure and
 * ends the path.
 *
 * An earlier version fell back to the remembered path whenever the reader
 * landed on any step of it. That made "click Genesis in the nav" silently put
 * someone back on a study they had left.
 *
 * What is remembered is the progress, not the state: the path and the step
 * reached, so the home page can offer to pick it up again. localStorage rather
 * than session, because "where you left off" only means anything if it
 * survives closing the tab -- the same storage, for the same reason, as
 * recentExplorers.
 */

const ACTIVE_PATH_KEY = "activeStudyPath";

function getStorage() {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }

  return { getItem: () => null, setItem() {}, removeItem() {} };
}

const storage = getStorage();

/**
 * Records the path and the step reached, so it can be resumed later.
 */
export function rememberStudyPath(pathId, progress = null) {
  if (!pathId) return;

  try {
    storage.setItem(
      ACTIVE_PATH_KEY,
      JSON.stringify({ pathId, ...(progress || {}), savedAt: Date.now() })
    );
  } catch (error) {
    // fail silently
  }
}

function readRemembered() {
  try {
    const value = storage.getItem(ACTIVE_PATH_KEY);
    if (!value) return null;

    /* Tolerates the bare id this used to store, so an in-flight reader does not
       lose their place when this ships. */
    if (!value.startsWith("{")) return { pathId: value };

    const parsed = JSON.parse(value);
    return parsed?.pathId ? parsed : null;
  } catch (error) {
    return null;
  }
}

/**
 * Where the reader got to: the path, the step, and the route to resume at.
 * Null until they have actually walked one, and null if the remembered route
 * is no longer part of that path -- an edited path should not strand anyone.
 */
export function getStudyPathProgress() {
  const remembered = readRemembered();
  if (!remembered?.route) return null;

  const located = locateInPath(remembered.pathId, remembered.route);
  if (!located) return null;

  return {
    ...located,
    route: remembered.route,
    /* Exposed so the home page can weigh this against the last page visited:
       whichever happened most recently is what the reader is carrying on. */
    savedAt: remembered.savedAt || 0,
    resumeTo: `${remembered.route}?path=${encodeURIComponent(remembered.pathId)}`,
  };
}

/**
 * Resolves the path in force on a given page, and where the page sits in it.
 *
 * Returns null when there is no path, or when this page is not one of its
 * steps. Both the strip and ContinueStory ask this, so they cannot disagree
 * about whether a path is running -- two "next" controls contradicting each
 * other on one page is the thing this feature exists to avoid.
 */
export function resolveActiveStudyPath(search, pathname) {
  const pathId = new URLSearchParams(search || "").get("path");
  if (!pathId) return null;

  const located = locateInPath(pathId, pathname);

  /* Only record a path the reader is genuinely walking. A stale or mistyped
     ?path= should not become the study they are offered to resume. */
  if (!located) return null;

  rememberStudyPath(pathId, {
    route: pathname,
    step: located.step,
    total: located.total,
  });

  return located;
}
